import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { Button } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import useStyles from './styles';

const SignatureAnswer = ({ editableAnswer, setEditableAnswer }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  // The signature is re-drawn from stored point data, so the ink colour is ours
  // to choose. Hardcoded '#000' made it invisible on a dark page.
  // `text.primary` is exactly black in the light theme.
  const theme = useTheme();
  const inkColor = theme.palette.text.primary;
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [currentStroke, setCurrentStroke] = useState([]);

  const parseData = (dataString) => {
    if (!dataString) return [];
    const points = dataString.split('_');
    const strokesArray = [];
    let currentStrokeArray = [];

    points.forEach((point) => {
      if (point === 'null,null') {
        if (currentStrokeArray.length > 0) {
          strokesArray.push(currentStrokeArray);
          currentStrokeArray = [];
        }
      } else {
        const [x, y] = point.split(',').map(Number);
        currentStrokeArray.push({ x, y });
      }
    });

    if (currentStrokeArray.length > 0) {
      strokesArray.push(currentStrokeArray);
    }

    return strokesArray;
  };

  const drawSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = inkColor;
    ctx.lineWidth = 2;

    strokes.forEach((stroke) => {
      if (stroke.length > 0) {
        ctx.beginPath();
        ctx.moveTo(stroke[0].x, stroke[0].y);
        for (let i = 1; i < stroke.length; i += 1) {
          ctx.lineTo(stroke[i].x, stroke[i].y);
        }
        ctx.stroke();
      }
    });

    if (currentStroke.length > 0) {
      ctx.beginPath();
      ctx.moveTo(currentStroke[0].x, currentStroke[0].y);
      for (let i = 1; i < currentStroke.length; i += 1) {
        ctx.lineTo(currentStroke[i].x, currentStroke[i].y);
      }
      ctx.stroke();
    }
  }, [strokes, currentStroke, inkColor]);

  useEffect(() => {
    if (editableAnswer && !isDrawing) {
      const parsedStrokes = parseData(editableAnswer);
      setStrokes(parsedStrokes);
    }
  }, [editableAnswer, isDrawing]);

  useEffect(() => {
    drawSignature();
  }, [drawSignature]);

  const getMousePos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getMousePos(e);
    setCurrentStroke([pos]);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const pos = getMousePos(e);
    setCurrentStroke((prev) => [...prev, pos]);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    if (currentStroke.length > 0) {
      const newStrokes = [...strokes, currentStroke];
      setStrokes(newStrokes);
      setCurrentStroke([]);

      const dataString = newStrokes.map((stroke) => stroke.map((p) => `${p.x},${p.y}`).join('_')).join('_null,null_');

      setEditableAnswer(dataString);
    }
  };

  const clearCanvas = () => {
    setStrokes([]);
    setCurrentStroke([]);
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setEditableAnswer('');
  };

  return (
    <div className={classes.content}>
      <div className={classes.signatureContainer}>
        <canvas
          ref={canvasRef}
          width={300}
          height={180}
          className={classes.canvas}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
        <div className={classes.buttonsContainer}>
          <Button
            onClick={clearCanvas}
            className={classes.clearButton}
          >
            {t('common.clean')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureAnswer;
